import { z } from 'zod'
import {
  STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION,
  stratalystFormsAuthoringAssignmentSchema,
  stratalystFormsAuthoringDb1SideEffectsSchema,
  stratalystFormsAuthoringModuleSchema,
  stratalystFormsAuthoringRecipeSchema,
  stratalystFormsAuthoringRevisionSchema,
} from './stratalyst-forms-authoring-read'

export const STRATALYST_FORMS_AUTHORING_WRITE_CONTRACT_VERSION = STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION

/**
 * Request schemas describe the transport shape for clients and tests. Domain
 * validation remains in the existing handlers so validation order and error
 * messages stay byte-identical to the pre-adoption routes.
 */
const optionalUnknown = z.unknown().optional()

export const stratalystFormsAuthoringModuleCreateRequestSchema = z.object({
  name: optionalUnknown,
  description: optionalUnknown,
  status: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringModuleUpdateRequestSchema = z.object({
  name: optionalUnknown,
  description: optionalUnknown,
  status: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringModuleFormAttachRequestSchema = z.object({
  form_registry_id: optionalUnknown,
  position: optionalUnknown,
  prompt_id: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringModuleFormPromptRequestSchema = z.object({
  prompt_id: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringRevisionCreateRequestSchema = z.object({
  fields: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringRevisionActivateRequestSchema = z.object({}).passthrough()

export const stratalystFormsAuthoringAssignmentCreateRequestSchema = z.object({
  module_id: optionalUnknown,
  org_id: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringAssignmentRevokeRequestSchema = z.object({}).passthrough()

export const stratalystFormsAuthoringRecipeCreateRequestSchema = z.object({
  name: optionalUnknown,
  steps: optionalUnknown,
}).passthrough()

export const stratalystFormsAuthoringRecipeActivateRequestSchema = z.object({}).passthrough()

export const stratalystFormsAuthoringModuleFormWriteSchema = z.object({
  id: z.string(),
  moduleId: z.string(),
  formRegistryId: z.string(),
  position: z.number(),
  promptId: z.string().nullable(),
}).passthrough()

const writeResponseBase = {
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_WRITE_CONTRACT_VERSION),
  replayed: z.boolean(),
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}

export const stratalystFormsAuthoringModuleCreateResponseSchema = z.object({
  ...writeResponseBase,
  module: stratalystFormsAuthoringModuleSchema,
}).passthrough()

export const stratalystFormsAuthoringModuleUpdateResponseSchema = z.object({
  ...writeResponseBase,
  module: stratalystFormsAuthoringModuleSchema,
}).passthrough()

export const stratalystFormsAuthoringModuleFormAttachResponseSchema = z.object({
  ...writeResponseBase,
  moduleForm: stratalystFormsAuthoringModuleFormWriteSchema,
}).passthrough()

export const stratalystFormsAuthoringModuleFormPromptResponseSchema = z.object({
  ...writeResponseBase,
  moduleForm: stratalystFormsAuthoringModuleFormWriteSchema,
}).passthrough()

export const stratalystFormsAuthoringRevisionCreateResponseSchema = z.object({
  ...writeResponseBase,
  revision: stratalystFormsAuthoringRevisionSchema,
}).passthrough()

export const stratalystFormsAuthoringRevisionActivateResponseSchema = z.object({
  ...writeResponseBase,
  revision: stratalystFormsAuthoringRevisionSchema,
}).passthrough()

export const stratalystFormsAuthoringAssignmentCreateResponseSchema = z.object({
  ...writeResponseBase,
  assignment: stratalystFormsAuthoringAssignmentSchema,
}).passthrough()

export const stratalystFormsAuthoringAssignmentRevokeResponseSchema = z.object({
  ...writeResponseBase,
  assignment: stratalystFormsAuthoringAssignmentSchema,
}).passthrough()

export const stratalystFormsAuthoringRecipeCreateResponseSchema = z.object({
  ...writeResponseBase,
  recipe: stratalystFormsAuthoringRecipeSchema,
}).passthrough()

export const stratalystFormsAuthoringRecipeActivateResponseSchema = z.object({
  ...writeResponseBase,
  recipe: stratalystFormsAuthoringRecipeSchema,
}).passthrough()

export type StratalystFormsAuthoringModuleCreateRequest = z.infer<typeof stratalystFormsAuthoringModuleCreateRequestSchema>
export type StratalystFormsAuthoringModuleUpdateRequest = z.infer<typeof stratalystFormsAuthoringModuleUpdateRequestSchema>
export type StratalystFormsAuthoringModuleFormAttachRequest = z.infer<typeof stratalystFormsAuthoringModuleFormAttachRequestSchema>
export type StratalystFormsAuthoringModuleFormPromptRequest = z.infer<typeof stratalystFormsAuthoringModuleFormPromptRequestSchema>
export type StratalystFormsAuthoringRevisionCreateRequest = z.infer<typeof stratalystFormsAuthoringRevisionCreateRequestSchema>
export type StratalystFormsAuthoringRevisionActivateRequest = z.infer<typeof stratalystFormsAuthoringRevisionActivateRequestSchema>
export type StratalystFormsAuthoringAssignmentCreateRequest = z.infer<typeof stratalystFormsAuthoringAssignmentCreateRequestSchema>
export type StratalystFormsAuthoringAssignmentRevokeRequest = z.infer<typeof stratalystFormsAuthoringAssignmentRevokeRequestSchema>
export type StratalystFormsAuthoringRecipeCreateRequest = z.infer<typeof stratalystFormsAuthoringRecipeCreateRequestSchema>
export type StratalystFormsAuthoringRecipeActivateRequest = z.infer<typeof stratalystFormsAuthoringRecipeActivateRequestSchema>
export type StratalystFormsAuthoringModuleCreateResponse = z.infer<typeof stratalystFormsAuthoringModuleCreateResponseSchema>
export type StratalystFormsAuthoringModuleUpdateResponse = z.infer<typeof stratalystFormsAuthoringModuleUpdateResponseSchema>
export type StratalystFormsAuthoringModuleFormAttachResponse = z.infer<typeof stratalystFormsAuthoringModuleFormAttachResponseSchema>
export type StratalystFormsAuthoringModuleFormPromptResponse = z.infer<typeof stratalystFormsAuthoringModuleFormPromptResponseSchema>
export type StratalystFormsAuthoringRevisionCreateResponse = z.infer<typeof stratalystFormsAuthoringRevisionCreateResponseSchema>
export type StratalystFormsAuthoringRevisionActivateResponse = z.infer<typeof stratalystFormsAuthoringRevisionActivateResponseSchema>
export type StratalystFormsAuthoringAssignmentCreateResponse = z.infer<typeof stratalystFormsAuthoringAssignmentCreateResponseSchema>
export type StratalystFormsAuthoringAssignmentRevokeResponse = z.infer<typeof stratalystFormsAuthoringAssignmentRevokeResponseSchema>
export type StratalystFormsAuthoringRecipeCreateResponse = z.infer<typeof stratalystFormsAuthoringRecipeCreateResponseSchema>
export type StratalystFormsAuthoringRecipeActivateResponse = z.infer<typeof stratalystFormsAuthoringRecipeActivateResponseSchema>
